import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    TissueType,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
} from "../models";

interface ITissueTypeQuery extends Pick<IUseQueryPaged<TissueType>, "pagedListQuery" | "listQuery" | "detailQuery"> {}

export const useTissueTypeQuery = (params?: QueryHookParams): ITissueTypeQuery => {
    const tissueTypeId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.TissueTypes);
    const url = Endpoints.TissueTypes;
    const queryType = getQueryType(tissueTypeId, params?.queryType);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<TissueType>>(getRequestOptions(url, config, queryParams, sortParams, pageParams)),
        queryKey: queryKeys.tissueTypes.pagedList({ ...queryParams, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<TissueType>>(
                getRequestOptions(url, config, queryParams, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.tissueTypes.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<TissueType>({ url: `${url}/${tissueTypeId}`, config }),
        queryKey: queryKeys.tissueTypes.detail(tissueTypeId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
    });

    return useMemo<ITissueTypeQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
        }),
        [pagedListQuery, listQuery, detailQuery]
    );
};
